import { getToken } from '@/utils/auth'

class WebSocketManager {
  constructor() {
    this.connections = new Map()
    this.reconnectAttempts = new Map()
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 3000
    this.messageHandlers = new Map() // Store multiple handlers per connection
  }

  connect(endpoint, roomName, callbacks = {}) {
    const token = getToken()
    if (!token) {
      console.error('No authentication token available')
      return null
    }
    const is_https = window.location.protocol == "https:"
    const ws_scheme = is_https ? "wss" : "ws";
    const ws_host = window.location.hostname;
    const ws_port =is_https ? '' : `:8000`  ;

    const wsUrl = `${ws_scheme}://${ws_host}${ws_port}/ws/${endpoint}/${roomName}/?token=${token}`
    const connectionKey = `${endpoint}_${roomName}`

    // If connection already exists, just add the new callbacks
    if (this.connections.has(connectionKey)) {
      const existingWs = this.connections.get(connectionKey)
      // Check if the connection is still open
      if (existingWs.readyState === WebSocket.OPEN) {
        console.log(`WebSocket connection already exists for ${endpoint}/${roomName}, adding new handlers`)
        this.addMessageHandlers(connectionKey, callbacks)
        return existingWs
      } else {
        // Connection exists but is not open, clean it up and create a new one
        console.log(`WebSocket connection exists but is not open for ${endpoint}/${roomName}, cleaning up and reconnecting`)
        this.connections.delete(connectionKey)
        this.messageHandlers.delete(connectionKey)
      }
    }

    const ws = new WebSocket(wsUrl)
    this.connections.set(connectionKey, ws)
    this.reconnectAttempts.set(connectionKey, 0)
    this.messageHandlers.set(connectionKey, [])

    ws.onopen = (event) => {
      console.log(`WebSocket connected: ${endpoint}/${roomName}`)
      this.reconnectAttempts.set(connectionKey, 0)
      this.notifyHandlers(connectionKey, 'onOpen', event)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        this.notifyHandlers(connectionKey, 'onMessage', data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    ws.onerror = (error) => {
      console.error(`WebSocket error: ${endpoint}/${roomName}`, error)
      this.notifyHandlers(connectionKey, 'onError', error)
      
      // If this is a connection error, we might want to clean up immediately
      if (ws.readyState === WebSocket.CLOSED) {
        this.connections.delete(connectionKey)
        this.messageHandlers.delete(connectionKey)
      }
    }

    ws.onclose = (event) => {
      console.log(`WebSocket closed: ${endpoint}/${roomName}`)
      this.connections.delete(connectionKey)
      this.messageHandlers.delete(connectionKey)
      
      this.notifyHandlers(connectionKey, 'onClose', event)
      
      // Auto-reconnect if not manually closed
      if (event.code !== 1000) {
        this.attemptReconnect(endpoint, roomName, callbacks, connectionKey)
      }
    }

    // Add the initial callbacks
    this.addMessageHandlers(connectionKey, callbacks)

    return ws
  }

  addMessageHandlers(connectionKey, callbacks) {
    const handlers = this.messageHandlers.get(connectionKey) || []
    handlers.push(callbacks)
    this.messageHandlers.set(connectionKey, handlers)
  }

  removeMessageHandlers(connectionKey, callbacks) {
    const handlers = this.messageHandlers.get(connectionKey) || []
    const index = handlers.indexOf(callbacks)
    if (index > -1) {
      handlers.splice(index, 1)
      this.messageHandlers.set(connectionKey, handlers)
    }
  }

  notifyHandlers(connectionKey, eventType, data) {
    const handlers = this.messageHandlers.get(connectionKey) || []
    handlers.forEach(handler => {
      if (handler[eventType]) {
        handler[eventType](data)
      }
    })
  }

  attemptReconnect(endpoint, roomName, callbacks, connectionKey) {
    const attempts = this.reconnectAttempts.get(connectionKey) || 0
    
    if (attempts < this.maxReconnectAttempts) {
      setTimeout(() => {
        console.log(`Attempting to reconnect (${attempts + 1}/${this.maxReconnectAttempts})`)
        this.reconnectAttempts.set(connectionKey, attempts + 1)
        this.connect(endpoint, roomName, callbacks)
      }, this.reconnectDelay)
    } else {
      console.error(`Max reconnection attempts reached for ${endpoint}/${roomName}`)
    }
  }

  send(endpoint, roomName, data) {
    const connectionKey = `${endpoint}_${roomName}`
    const ws = this.connections.get(connectionKey)
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data))
    } else {
      console.error(`WebSocket not connected: ${endpoint}/${roomName}`)
    }
  }

  disconnect(connectionKey, callbacks = null) {
    if (callbacks) {
      // Remove specific handlers
      this.removeMessageHandlers(connectionKey, callbacks)
      
      // Check if there are any remaining handlers
      const remainingHandlers = this.messageHandlers.get(connectionKey) || []
      if (remainingHandlers.length === 0) {
        // No more handlers, close the connection
        const ws = this.connections.get(connectionKey)
        if (ws) {
          ws.close(1000, 'Manual disconnect')
          this.connections.delete(connectionKey)
          this.messageHandlers.delete(connectionKey)
        }
      }
    } else {
      // Disconnect completely
      const ws = this.connections.get(connectionKey)
      if (ws) {
        ws.close(1000, 'Manual disconnect')
        this.connections.delete(connectionKey)
        this.messageHandlers.delete(connectionKey)
      }
    }
  }

  disconnectAll() {
    this.connections.forEach((ws) => {
      ws.close(1000, 'Disconnect all')
    })
    this.connections.clear()
  }
}

export default new WebSocketManager()
